// salvagers-api as the pages see it: typed calls and the query keys that
// cache them. A write invalidates the keys it changes.

import { apiJson, apiRequest, json, publicJson, publicRequest } from "@common/api";

export interface Me {
  sub: string;
  email: string;
  groups: string[];
  admin: boolean;
}

export interface Profile {
  user_id: string;
  display_name: string;
  updated_at: number;
}

export type BannerLevel = "info" | "warning";

export interface Banner {
  text: string;
  level: BannerLevel;
  enabled: boolean;
}

export interface StoredBanner {
  banner: Banner | null;
  updated_at: number | null;
  updated_by: string | null;
}

export interface TableInfo {
  key: string;
  name: string;
}

export interface TableRows extends TableInfo {
  items: Record<string, unknown>[];
}

export interface AdminMember {
  sub: string;
  email: string | null;
}

export type DeployComponent = "terraform" | "api" | "ui";

export interface Deploy {
  component: DeployComponent;
  /** RFC 3339, or null when deploy.sh never recorded one. */
  deployed_at: string | null;
  deployed_by: string | null;
}

export interface LambdaFunction {
  name: string;
  /** null for a container image. */
  runtime: string | null;
  /** When the code or configuration last changed; ISO 8601 UTC. */
  last_modified: string | null;
  /** The newest event in its log group; null when it has never logged or the logs expired. */
  last_invoked_at: string | null;
}

/** One address in the central visitor log. */
export interface Visitor {
  ip: string;
  /** Sessions: a visit after 5 minutes idle starts a new one. */
  count: number;
  /** RFC 3339 UTC, or null when missing. */
  first_seen: string | null;
  last_seen: string | null;
}

export const queryKeys = {
  me: ["me"] as const,
  profile: ["me", "profile"] as const,
  banner: ["banner"] as const,
  adminBanner: ["admin", "banner"] as const,
  tables: ["admin", "tables"] as const,
  table: (key: string) => ["admin", "tables", key] as const,
  members: ["admin", "members"] as const,
  deploys: ["admin", "deploys"] as const,
  lambdas: ["admin", "lambdas"] as const,
  visitors: ["admin", "visitors"] as const,
};

export const getMe = () => apiJson<Me>("/me");
export const getProfile = () => apiJson<Profile>("/me/profile");
export const putProfile = (displayName: string) =>
  apiJson<Profile>("/me/profile", { method: "PUT", ...json({ display_name: displayName }) });

/** Public: the front page's "keep me posted" form. `website` is the honeypot; leave it empty. */
export const signUp = async (email: string, website = "") => {
  await publicRequest("/signups", { method: "POST", ...json({ email, website }) });
};

/** Public: what every visitor sees, or null. */
export const getBanner = () => publicJson<{ banner: Banner | null }>("/banner").then((body) => body.banner);

export const admin = {
  banner: () => apiJson<StoredBanner>("/admin/banner"),
  setBanner: (banner: Banner) => apiJson<Banner>("/admin/banner", { method: "PUT", ...json(banner) }),
  clearBanner: async () => {
    await apiRequest("/admin/banner", { method: "DELETE" });
  },
  tables: () => apiJson<TableInfo[]>("/admin/tables"),
  table: (key: string) => apiJson<TableRows>(`/admin/tables/${encodeURIComponent(key)}`),
  members: () => apiJson<AdminMember[]>("/admin/members"),
  addMember: (email: string) => apiJson<AdminMember>("/admin/members", { method: "POST", ...json({ email }) }),
  removeMember: async (sub: string) => {
    await apiRequest(`/admin/members/${encodeURIComponent(sub)}`, { method: "DELETE" });
  },
  deploys: () => apiJson<Deploy[]>("/admin/deploys"),
  lambdas: () => apiJson<LambdaFunction[]>("/admin/lambdas"),
  /** Most recent first, at most 1000. */
  visitors: () => apiJson<Visitor[]>("/admin/visitors"),
};
